import React, {Suspense} from "react"
import {BrowserRouter, Routes, Route, useParams, useLocation, Navigate} from "react-router-dom"
import Cookies from "js-cookie"

import RouteErrorBoundary from "./components/RouteErrorBoundary"
import LoadingSpinner from "./components/LoadingSpinner"
import { useAuth } from "./contexts/AuthContext"
import { AdminSocketProvider } from "./contexts/AdminSocketContext"

import AdminSidebar from "./components/AdminSidebar"

const Vote = React.lazy(() => import("./components/Vote"))
const VoteLegacy = React.lazy(() => import("./components/Vote/VoteLegacy"))
const DemoInviteVariants = React.lazy(() => import("./containers/DemoInviteVariants"))
const Abfrage = React.lazy(() => import("./components/Abfrage"))
const Erstellen = React.lazy(() => import("./components/Erstellen"))
const Signup = React.lazy(() => import("./components/Signup"))
const SignupRedesignHub     = React.lazy(() => import("./containers/SignupRedesign/Hub"))
const SignupRedesignWizard  = React.lazy(() => import("./containers/SignupRedesign/WizardConcept"))
const SignupRedesignSidebar = React.lazy(() => import("./containers/SignupRedesign/SidebarConcept"))
const SignupRedesignCards   = React.lazy(() => import("./containers/SignupRedesign/CardsConcept"))
const SidebarLab            = React.lazy(() => import("./containers/SignupRedesign/SidebarLab"))
const FragenLab             = React.lazy(() => import("./containers/SignupRedesign/FragenLab"))
const VoteTailwind          = React.lazy(() => import("./containers/SignupRedesign/VoteTailwind"))
const DesignSystem          = React.lazy(() => import("./containers/DesignSystem/DesignSystem"))
const VoteCardConcepts      = React.lazy(() => import("./containers/SignupRedesign/VoteCardConcepts"))
const ErstellenV2           = React.lazy(() => import("./containers/ErstellenV2/ErstellenV2"))
const SignupV2              = React.lazy(() => import("./containers/SignupV2"))
const Custom_live_guideline = React.lazy(() => import("./containers/Custom_live_guideline"))
const StageLeitlinien = React.lazy(() => import("./containers/StageLeitlinien"))
const LiveLeitlinien = React.lazy(() => import("./containers/StageLeitlinien/LiveLeitlinien.js"))
const QualityIndicators = React.lazy(() => import("./containers/QualityIndicators"))
const Create_Custom_live_guideline = React.lazy(() => import("./containers/Custom_live_guideline/Create.js"))
const Edit_Custom_live_guideline = React.lazy(() => import("./containers/Custom_live_guideline/Edit.js"))
const UserList = React.lazy(() => import("./components/UserList"))
const Guidelines = React.lazy(() => import("./components/Guidelines"))
const Consultation = React.lazy(() => import("./components/Consultation"))
const Onkospace_com = React.lazy(() => import("./components/onkospace.com"))
const Mandatierung = React.lazy(() => import("./components/FachgesellschaftenTicket"))
const EmailVorlagen     = React.lazy(() => import("./components/EmailVorlagen"))
const EmailVorlagenEdit = React.lazy(() => import("./components/EmailVorlagen/Edit"))
const FragenVorlagen     = React.lazy(() => import("./containers/FragenVorlagen"))
const FragenVorlagenEdit = React.lazy(() => import("./containers/FragenVorlagen/Edit"))
const Allgemein = React.lazy(() => import("./components/Allgemein"))
const ErrorsDisplay = React.lazy(() => import("./components/ErrorsDisplay"))

const Impressum = React.lazy(() => import("./containers/Impressum"))
const Datenschutz = React.lazy(() => import("./containers/Datenschutz"))

const DeepSearch = React.lazy(() => import("./containers/DeepSearch"))
const GuidelineSurveys = React.lazy(() => import("./containers/GuidelineSurveys"))
const SurveyEditor = React.lazy(() => import("./containers/SurveyEditor"))
const SurveyResults = React.lazy(() => import("./containers/SurveyResults"))
const GuidelineComments = React.lazy(() => import("./containers/GuidelineComments"))
const AppPush = React.lazy(() => import("./containers/AppPush"))
const AppAnalytics = React.lazy(() => import("./containers/AppAnalytics"))
const BrokenURLs = React.lazy(() => import("./containers/BrokenURLs"))
const BrokenUrlsProgress = React.lazy(() => import("./containers/BrokenURLs/BrokenUrlsProgress.js"))
const BrokenUrlsIgnored = React.lazy(() => import("./containers/BrokenURLs/BrokenUrlsIgnored.js"))
const BrokenURLReport = React.lazy(() => import("./containers/BrokenURLs/SearchResults.js"))
const ServerMetrics = React.lazy(() => import("./containers/ServerMetrics"))
const HubAnalytics = React.lazy(() => import("./containers/HubAnalytics"))
const AbstimmungsAnalytics = React.lazy(() => import("./containers/AbstimmungsAnalytics/AbstimmungsAnalytics"))
const VoteFeedback = React.lazy(() => import("./containers/VoteFeedback/VoteFeedback"))
const EmailDeliverability = React.lazy(() => import("./containers/EmailDeliverability"))
const Backups = React.lazy(() => import("./containers/Backups/Backups"))
const Newsletter = React.lazy(() => import("./containers/Newsletter"))
const NewsletterCompose = React.lazy(() => import("./containers/Newsletter/NewsletterCompose"))
const NewsletterHistory = React.lazy(() => import("./containers/Newsletter/NewsletterHistory"))
const NewsletterTransactional = React.lazy(() => import("./containers/Newsletter/NewsletterTransactional"))


const NotFound = React.lazy(() => import("./containers/NotFound"))

const UserVote_Test = React.lazy(() => import("./containers/UserVote_Test"))

const VoteResult_Public = React.lazy(() => import("./containers/ResultsLoader/PublicResult"))
const VoteResult_Public_V2 = React.lazy(() => import("./containers/ResultsLoaderNEW/PublicResult"))

const FachgesellschaftenGraph = React.lazy(() => import("./containers/FachgesellschaftenGraph"))
const Unsubscribe = React.lazy(() => import("./containers/Unsubscribe"))

const Login = React.lazy(() => import("./components/Login"))
const ForgotPassword = React.lazy(() => import("./components/Login/ForgotPassword"))
const AcceptInvite = React.lazy(() => import("./containers/AcceptInvite/AcceptInvite"))
const ConfirmEmail = React.lazy(() => import("./containers/ConfirmEmail/ConfirmEmail"))
const ResetPassword = React.lazy(() => import("./containers/ResetPassword/ResetPassword"))
const Profile = React.lazy(() => import("./components/Profile/Profile"))
const AdminList = React.lazy(() => import("./components/AdminList/AdminList"))
const AdminForm = React.lazy(() => import("./containers/AdminForm/AdminForm"))
const AuditLog = React.lazy(() => import("./containers/AuditLog/AuditLog"))

import Test from "./Test"

// Inline panel style for v2 pages , overrides the legacy .main-panel chrome
// (white bg + 20% radius + 8px padding) so v2 content fills the panel with
// the warm cream bg and no inner card. Used by every page wrapper that
// renders a v2-styled inner component.
const V2_PANEL_STYLE = {
  minHeight: "100vh",
  padding: 0,
  background: "#fafaf7",
  borderRadius: 0,
}

function ImpressumPage() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Impressum />
    </Suspense>
  )
}

function Custom_live_guidelinePage() {
  return (
    <>
      <AdminSidebar openedTab={"Leitlinien"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <Custom_live_guideline />
        </Suspense>
      </div>
    </>
  )
}

function ConsultationPage() {
  return (
    <>
      <AdminSidebar openedTab={"Consultation"} variant="v2" />
      <div className="main-panel main-panel--v2" style={{minHeight: "100vh"}}>
        <Suspense fallback={<LoadingSpinner />}>
          <Consultation />
        </Suspense>
      </div>
    </>
  )
}

function DeepSearchPage() {
  return (
    <>
      <AdminSidebar openedTab={"Leitlinien"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <DeepSearch />
        </Suspense>
      </div>
    </>
  )
}

function BrokenURLsPage() {
  return (
    <>
      <AdminSidebar openedTab={"Leitlinien"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <BrokenURLs />
        </Suspense>
      </div>
    </>
  )
}

function GuidelineSurveysPage() {
  return (
    <>
      <AdminSidebar openedTab={"Mobile App"} variant="v2" desktopBreakpoint="lg" />
      <div className="main-panel main-panel--sidebar-lg" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <GuidelineSurveys />
        </Suspense>
      </div>
    </>
  )
}

// Settings merged into the editor (2026-09); old bookmarks land on the editor.
function SurveyEditorSettingsRedirect() {
  const { id } = useParams()
  return <Navigate to={`/leitlinien-umfragen/editor/${id}`} replace />
}

function SurveyEditorPage() {
  return (
    <>
      <AdminSidebar openedTab={"Mobile App"} variant="v2" desktopBreakpoint="lg" />
      <div className="main-panel main-panel--sidebar-lg" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <SurveyEditor />
        </Suspense>
      </div>
    </>
  )
}

function SurveyResultsPage() {
  return (
    <>
      <AdminSidebar openedTab={"Mobile App"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <SurveyResults />
        </Suspense>
      </div>
    </>
  )
}

function GuidelineCommentsPage() {
  return (
    <>
      <AdminSidebar openedTab={"Mobile App"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <GuidelineComments />
        </Suspense>
      </div>
    </>
  )
}

function AppPushPage() {
  return (
    <>
      <AdminSidebar openedTab={"Mobile App"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <AppPush />
        </Suspense>
      </div>
    </>
  )
}

function AppAnalyticsPage() {
  return (
    <>
      <AdminSidebar openedTab={"Mobile App"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <AppAnalytics />
        </Suspense>
      </div>
    </>
  )
}

function PrivateBrokenURLsGuidelinePage() {
  let {guideline_id } = useParams()
  return (
    <>
      <AdminSidebar openedTab={"Leitlinien"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
           <BrokenURLReport guidelineId={guideline_id} />
        </Suspense>
      </div>
    </>
  )
}

function PublicBrokenURLsPage() {
  // Public, login-free broken-URL report. Renders the read-only view standalone
  // (no admin sidebar) and fetches the no-auth /public-report endpoint, so an
  // admin can share a stable /brokenurls/<slug> link with anyone. Signed-in
  // admins hit the private route instead (full report + ignore actions).
  const { guideline_id } = useParams()
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <BrokenURLReport guidelineId={guideline_id} readOnly publicMode />
    </Suspense>
  )
}

function BrokenUrlsProgressPage() {
  return (
    <>
      <AdminSidebar openedTab={"Leitlinien"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <BrokenUrlsProgress />
        </Suspense>
      </div>
    </>
  )
}

function BrokenUrlsIgnoredPage() {
  return (
    <>
      <AdminSidebar openedTab={"Leitlinien"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <BrokenUrlsIgnored />
        </Suspense>
      </div>
    </>
  )
}


function TestBanner() {
  if (!window.location.href?.includes('test')) return null
  return (
    <div style={{
      position: 'fixed',
      top: '5px',
      right: '10px',
      color: 'red',
      fontSize: '24px',
      fontWeight: 'bold',
      textShadow: '0 0 10px red',
      zIndex: 9999999999900
    }}>
      TEST
    </div>
  )
}

// Newsletter pages share a permission gate: the sidebar uses
// `permissionKey: 'newsletter'` to hide the entry, but the route mounts
// regardless of permission. Backend send/admin endpoints require admin or
// super_admin roles, so a manager landing here saw the UI then 403'd on
// every API call. This wrapper either renders the page (when permitted)
// or shows a clear "no access" message — matches the brokenurls pattern.
function NewsletterGate({ children, manageOnly = false }) {
  const { hasPermission, hasRole } = useAuth()
  // `hasPermission` already returns true for admin/super_admin (see
  // AuthContext: short-circuit on role). So the single check covers
  // both "has newsletter page-permission" AND "is admin." Send actions
  // further gate on role at the backend; the UI just needs to allow entry
  // without crashing on the API calls.
  const canEnter = hasPermission('newsletter') && (!manageOnly || hasRole('admin', 'super_admin'))
  if (!canEnter) {
    return (
      <>
        <AdminSidebar openedTab={"Newsletter"} variant="v2" />
        <div className="main-panel" style={V2_PANEL_STYLE}>
          <div className="max-w-[600px] mx-auto px-6 pt-16 text-center">
            <h2 className="m-0 text-[18px] font-semibold text-[#1a1816]">Kein Zugriff</h2>
            <p className="m-0 mt-2 text-[13px] text-[#6b6862]">
              Für den Newsletter-Bereich fehlen Ihrem Konto die Berechtigungen.
              Bitte wenden Sie sich an einen Administrator.
            </p>
          </div>
        </div>
      </>
    )
  }
  return (
    <>
      <AdminSidebar openedTab={"Newsletter"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          {children}
        </Suspense>
      </div>
    </>
  )
}

function NewsletterPage() { return <NewsletterGate><Newsletter /></NewsletterGate> }
function NewsletterComposePage() { return <NewsletterGate manageOnly><NewsletterCompose /></NewsletterGate> }
function NewsletterHistoryPage() { return <NewsletterGate><NewsletterHistory /></NewsletterGate> }
function NewsletterTransactionalPage() { return <NewsletterGate manageOnly><NewsletterTransactional /></NewsletterGate> }



function HubAnalyticsPage() {
  return (
    <>
      <AdminSidebar openedTab={"Leitlinien"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <HubAnalytics />
        </Suspense>
      </div>
    </>
  )
}

function AbstimmungsAnalyticsPage() {
  return (
    <>
      <AdminSidebar openedTab={"Umfragen"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <AbstimmungsAnalytics />
        </Suspense>
      </div>
    </>
  )
}

function VoteFeedbackPage() {
  return (
    <>
      <AdminSidebar openedTab={"Feedback"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}><VoteFeedback /></Suspense>
      </div>
    </>
  )
}

const ROUTE_PERMISSIONS = [
  ['/abstimmungen-analytics', 'abstimmungen-analytics'], ['/feedback', 'feedback'],
  ['/abstimmungen-alt', 'abstimmungen'], ['/abstimmungen', 'abstimmungen'], ['/abstimmung/', 'abstimmungen'],
  ['/konsultation', 'konsultation'], ['/abfragen', 'abfragen'], ['/abfrage/', 'abfragen'],
  ['/authors', 'authors'], ['/fragenvorlagen', 'fragenvorlagen'],
  ['/activity', 'activity'], ['/leitlinien', 'leitlinien'], ['/stageleitlinien', 'stageleitlinien'],
  ['/livearbeitsversionen', 'livearbeitsversionen'], ['/hub-analytics', 'hub-analytics'],
  ['/hub-leitlinien', 'hub-leitlinien'], ['/app-analytics', 'app-analytics'],
  ['/app-benachrichtigungen', 'app-benachrichtigungen'], ['/leitlinien-umfragen', 'leitlinien-umfragen'],
  ['/leitlinien-kommentare', 'leitlinien-kommentare'], ['/newsletter', 'newsletter'],
  ['/emailvorlagen', 'emailvorlagen'], ['/deepsearch', 'deepsearch'], ['/brokenurls', 'brokenurls'],
  ['/fachgesellschaftengraph', 'fachgesellschaftengraph'],
]

function RouteAccessBoundary({ children }) {
  const location = useLocation()
  const { hasPermission, hasRole, canPerformAction } = useAuth()
  const path = location.pathname
  let allowed = true
  if (path.startsWith('/admin-verwaltung') || path.startsWith('/audit-log') || path.startsWith('/email-zustellung')) {
    allowed = hasRole('admin', 'super_admin')
  } else if (path.startsWith('/errorsdisplay') || path.startsWith('/backups')) {
    allowed = hasRole('super_admin')
  } else {
    const match = ROUTE_PERMISSIONS.find(([prefix]) => path === prefix || path.startsWith(prefix))
    if (match) allowed = hasPermission(match[1])
  }

  if (allowed && path.startsWith('/abstimmung/erstellen')) allowed = canPerformAction('abstimmungen', 'create')
  if (allowed && path.startsWith('/abstimmung/bearbeiten')) allowed = canPerformAction('abstimmungen', 'edit')
  if (allowed && path.startsWith('/konsultation/erstellen')) allowed = canPerformAction('konsultation', 'create')
  if (allowed && path.startsWith('/konsultation/bearbeiten')) allowed = canPerformAction('konsultation', 'edit')

  if (allowed) return children
  return (
    <>
      <AdminSidebar variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <div className="max-w-[560px] mx-auto px-6 pt-20 text-center">
          <h1 className="m-0 text-[22px] font-semibold text-[#1a1816]">Kein Zugriff</h1>
          <p className="m-0 mt-2 text-[13px] text-[#6b6862]">Diese Seite oder Aktion ist für Ihre Rolle nicht freigegeben.</p>
          <button type="button" onClick={() => window.history.back()} className="mt-5 rounded-[9px] bg-[#e85d1f] px-4 py-2 text-[12.5px] font-semibold text-white">Zurück</button>
        </div>
      </div>
    </>
  )
}

function EmailDeliverabilityPage() {
  return (
    <>
      <AdminSidebar openedTab={"Tools"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <EmailDeliverability />
        </Suspense>
      </div>
    </>
  )
}

function ServerMetricsPage() {
  return (
    <>
      <AdminSidebar openedTab={"Leitlinien"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <ServerMetrics />
        </Suspense>
      </div>
    </>
  )
}

function StageLeitlinienPage() {
  return (
    <>
      <AdminSidebar openedTab={"StageLeitlinien"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <StageLeitlinien />
        </Suspense>
      </div>
    </>
  )
}

function LiveLeitlinienPage() {
  return (
    <>
      <AdminSidebar openedTab={"StageLeitlinien"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <LiveLeitlinien />
        </Suspense>
      </div>
    </>
  )
}

function QualityIndicatorsPage() {
  return (
    <>
      <AdminSidebar openedTab={"QualityIndicators"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <QualityIndicators />
        </Suspense>
      </div>
    </>
  )
}

function Create_Custom_live_guidelinePage() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Create_Custom_live_guideline />
    </Suspense>
  )
}
function Edit_Custom_live_guidelinePage() {
  let {guidelineId } = useParams()
  return (
    <>
      <AdminSidebar openedTab={"Leitlinien"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <Edit_Custom_live_guideline guidelineId={guidelineId}/>
        </Suspense>
      </div>
    </>
  )
}

function DatenschutzPage() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Datenschutz />
    </Suspense>
  )
}

function VoteResult_PublicPage() {
  let {vote_id, tab, element_uid} = useParams()
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <VoteResult_Public vote_id={vote_id} tab={tab} element_uid={element_uid} />
    </Suspense>
  )
}

function VoteResult_PublicPage_V2() {
  const params = useParams()
  const vote_id = params.vote_id
  const wildcardTab = (params['*'] || '').split('/').filter(Boolean).pop()
  const tab = params.tab || (['allgemein', 'teilnehmende', 'export'].includes(wildcardTab) ? wildcardTab : undefined)
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <VoteResult_Public_V2 vote_id={vote_id} tab={tab} />
    </Suspense>
  )
}

function UserVote_TestPage() {
  let {url} = useParams()

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <UserVote_Test url={url} />
    </Suspense>
  )
}

// The standalone visualisation used to live at /fachgesellschaften and was
// opened in a new tab from /fachgesellschaftengraph. Both are now one tabbed
// page; these redirects keep old links and bookmarks working.
function FachgesellschaftenRedirect() {
  return <Navigate replace to="/fachgesellschaftengraph?tab=tabellen" />
}

function FachgesellschaftenLeitlinieRedirect() {
  let {guidline_id} = useParams()
  return (
    <Navigate
      replace
      to={`/fachgesellschaftengraph?tab=tabellen&leitlinie=${encodeURIComponent(guidline_id || '')}`}
    />
  )
}

function UnsubscribePage() {
  let {user} = useParams()
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Unsubscribe user={user} />
    </Suspense>
  )
}

function ActivityPage() {
  return (
    <>
      <AdminSidebar openedTab={"Allgemein"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <Allgemein />
        </Suspense>
      </div>
    </>
  )
}

function ErrorsDisplayPage() {
  return (
    <>
      <AdminSidebar openedTab={"ErrorsDisplay"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <ErrorsDisplay />
        </Suspense>
      </div>
    </>
  )
}

function BackupsPage() {
  return (
    <>
      <AdminSidebar openedTab={"Backups"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <Backups />
        </Suspense>
      </div>
    </>
  )
}

function AuthorsPage() {
  return (
    <>
      <AdminSidebar openedTab={"UserList"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <UserList />
        </Suspense>
      </div>
    </>
  )
}

function LeitlinienPage() {
  return (
    <>
      <AdminSidebar openedTab={"Guidelines"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <Guidelines />
        </Suspense>
      </div>
    </>
  )
}

function AbfragenPage() {
  return (
    <>
      <AdminSidebar openedTab={"Abfrage"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <Abfrage />
        </Suspense>
      </div>
    </>
  )
}

// /:type/erstellen/ (catch-all) , redirects to the canonical typed picker
// route so the v2 chrome (sidebar + ErstellenV2 picker) renders.
function ErstellenPage() {
  const {type} = useParams()
  const canonical = type === 'abfrage' ? '/abfrage/erstellen'
                  : type === 'konsultation' ? '/konsultation/erstellen'
                  : '/abstimmung/erstellen'
  return <Navigate replace to={canonical}/>
}

function AbfrageEditPage() {
  let {id} = useParams()
  return (
    <>
      <AdminSidebar openedTab={"Abfrage"} variant="v2" />
      <div className="main-panel p-0" style={{minHeight: "100vh"}}>
        <Suspense fallback={<LoadingSpinner />}>
          <Signup type={"Umfrage Updatebedarf"} signup_bool={false} edit_bool={true} link={"/api/abfrage/single/" + id} redirect_after_submit="/abfragen" />
        </Suspense>
      </div>
    </>
  )
}

// AbfrageErstellenPage , canonical /abfrage/erstellen/:guideline_id route.
// Renders the v2 form (SignupV2). Same wrapper as AbfrageErstellenPageV2.
function AbfrageErstellenPage() {
  return <AbfrageErstellenPageV2/>
}

function AbstimmungenPage() {
  // Pass the granular permission predicates into the class component so it
  // can hide "Neue Abstimmung" + per-row Einladen/Bearbeiten affordances
  // based on what the manager is actually allowed to do. Backend enforces
  // each mutation again as the source of truth.
  const { canPerformAction, hasSurveyAccess } = useAuth()
  return (
    <>
      <AdminSidebar openedTab={"Vote"} variant="v2" />
      <div className="main-panel main-panel--v2" style={{minHeight: "100vh"}}>
        <Suspense fallback={<LoadingSpinner />}>
          <Vote
            variant="v2"
            canCreate={canPerformAction('abstimmungen', 'create')}
            canEditAll={canPerformAction('abstimmungen', 'edit')}
            canInviteAll={canPerformAction('abstimmungen', 'invite')}
            hasGuidelineAccess={(guideline) => hasSurveyAccess('abstimmungen', guideline)}
          />
        </Suspense>
      </div>
    </>
  )
}

function AbstimmungenLegacyPage() {
  // Frozen legacy listing — the "Alte Abstimmungen" page. Renders only the
  // pre-v2 abstimmungen (?flow=legacy) and forces canCreate=false so there is
  // no "Neue Abstimmung" entry point. Edit/results route to the classic pages.
  const { canPerformAction, hasSurveyAccess } = useAuth()
  return (
    <>
      <AdminSidebar openedTab={"Vote"} variant="v2" />
      <div className="main-panel main-panel--v2" style={{minHeight: "100vh"}}>
        <Suspense fallback={<LoadingSpinner />}>
          <VoteLegacy
            variant="v2"
            canCreate={false}
            canEditAll={canPerformAction('abstimmungen', 'edit')}
            canInviteAll={canPerformAction('abstimmungen', 'invite')}
            hasGuidelineAccess={(guideline) => hasSurveyAccess('abstimmungen', guideline)}
          />
        </Suspense>
      </div>
    </>
  )
}

function AbstimmungEditPage() {
  let {id} = useParams()
  return (
    <>
      <AdminSidebar openedTab={"Vote"} variant="v2" />
      <div className="main-panel p-0" style={{minHeight: "100vh"}}>
        <Suspense fallback={<LoadingSpinner />}>
          {/* Classic editor is reached only from the legacy "Alte Abstimmungen"
              page now, so return there after saving (not the v2 list). */}
          <Signup type={"Abstimmung"} signup_bool={false} edit_bool={true} link={"/api/vote/single/" + id} redirect_after_submit="/abstimmungen-alt" />
        </Suspense>
      </div>
    </>
  )
}

function KonsultationEditPage() {
  let {id} = useParams()
  return (
    <>
      <AdminSidebar openedTab={"Konsultation"} />
      <div className="main-panel p-0" style={{minHeight: "100vh"}}>
        <Suspense fallback={<LoadingSpinner />}>
          <Signup type={"konsultation"} signup_bool={false} edit_bool={true} link={"/api/consultation/single/" + id} redirect_after_submit="/konsultation" />
        </Suspense>
      </div>
    </>
  )
}

// Legacy /<type>/erstellen_v2 URLs redirect to the canonical /erstellen
// URLs so old bookmarks still work but the URL bar settles on the clean
// name. The v2 form/picker is rendered directly by the /erstellen routes
// further down — see AbstimmungErstellenPageV2 / AbstimmungErstellenPickerV2.
function AbstimmungErstellenV2Redirect() {
  const {guideline_id, template_id} = useParams()
  const suffix = guideline_id
    ? (template_id ? `/${guideline_id}/${template_id}` : `/${guideline_id}`)
    : ''
  return <Navigate replace to={`/abstimmung/erstellen${suffix}`}/>
}
function AbfrageErstellenV2Redirect() {
  const {guideline_id, template_id} = useParams()
  const suffix = guideline_id
    ? (template_id ? `/${guideline_id}/${template_id}` : `/${guideline_id}`)
    : ''
  return <Navigate replace to={`/abfrage/erstellen${suffix}`}/>
}
function KonsultationErstellenV2Redirect() {
  const {guideline_id, template_id} = useParams()
  const suffix = guideline_id
    ? (template_id ? `/${guideline_id}/${template_id}` : `/${guideline_id}`)
    : ''
  return <Navigate replace to={`/konsultation/erstellen${suffix}`}/>
}

// Canonical /abstimmung/erstellen/:guideline_id and /konsultation/erstellen/:guideline_id
// routes , both render the v2 form via the existing v2 page wrappers below.
function AbstimmungErstellenPage() { return <AbstimmungErstellenPageV2/> }
function KonsultationErstellenPage() { return <KonsultationErstellenPageV2/> }

// ─── SignupV2 page wrappers — same props contract as v1 Signup so routes are 1-to-1 ───
function AbstimmungEditPageV2() {
  const {id} = useParams()
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AdminSidebar openedTab={"Abstimmungen"} variant="v2" />
      <SignupV2 type={"Abstimmung"} signup_bool={false} edit_bool={true} link={"/api/vote/single/" + id} redirect_after_submit="/abstimmungen" />
    </Suspense>
  )
}
function AbstimmungErstellenPageV2() {
  const {guideline_id, template_id} = useParams()
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AdminSidebar openedTab={"Abstimmungen"} variant="v2" />
      <SignupV2 type={"Abstimmung"} signup_bool={true} edit_bool={false} guideline_id={guideline_id} template_id={template_id} link={"/api/vote"} redirect_after_submit="/abstimmungen" />
    </Suspense>
  )
}
function AbfrageEditPageV2() {
  const {id} = useParams()
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AdminSidebar openedTab={"Umfragen"} variant="v2" />
      <SignupV2 type={"Umfrage Updatebedarf"} signup_bool={false} edit_bool={true} link={"/api/abfrage/single/" + id} redirect_after_submit="/abfragen" />
    </Suspense>
  )
}
function AbfrageErstellenPageV2() {
  const {guideline_id, template_id} = useParams()
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AdminSidebar openedTab={"Umfragen"} variant="v2" />
      <SignupV2 type={"Umfrage Updatebedarf"} signup_bool={true} edit_bool={false} guideline_id={guideline_id} template_id={template_id} link={"/api/abfrage"} redirect_after_submit="/abfragen" />
    </Suspense>
  )
}
function KonsultationEditPageV2() {
  const {id} = useParams()
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AdminSidebar openedTab={"Consultation"} variant="v2" />
      <SignupV2 type={"konsultation"} signup_bool={false} edit_bool={true} link={"/api/consultation/single/" + id} redirect_after_submit="/konsultation" />
    </Suspense>
  )
}
function KonsultationErstellenPageV2() {
  const {guideline_id, template_id} = useParams()
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AdminSidebar openedTab={"Consultation"} variant="v2" />
      <SignupV2 type={"konsultation"} signup_bool={true} edit_bool={false} guideline_id={guideline_id} template_id={template_id} link={"/api/consultation"} redirect_after_submit="/konsultation" />
    </Suspense>
  )
}

// ─── v2 Erstellen picker (guideline-only, no template step) ───────────────
function AbstimmungErstellenPickerV2() {
  return (
    <>
      <AdminSidebar openedTab={"Vote"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <ErstellenV2 type="Abstimmung" />
        </Suspense>
      </div>
    </>
  )
}
function AbfrageErstellenPickerV2() {
  return (
    <>
      <AdminSidebar openedTab={"Umfragen"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <ErstellenV2 type="Umfrage Updatebedarf" />
        </Suspense>
      </div>
    </>
  )
}
function KonsultationErstellenPickerV2() {
  return (
    <>
      <AdminSidebar openedTab={"Consultation"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <ErstellenV2 type="konsultation" />
        </Suspense>
      </div>
    </>
  )
}

// function KonsultationenPage() {
//   return (
//     <>
//       <AdminSidebar openedTab={"Consultation"} />
//       <div className="main-panel" style={V2_PANEL_STYLE}>
//         <Suspense fallback={<LoadingSpinner />}>
//           <Consultation />
//         </Suspense>
//       </div>
//     </>
//   )
// }

function FragenVorlagenPage() {
  return (
    <>
      <AdminSidebar openedTab={"FragenVorlagen"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <FragenVorlagen />
        </Suspense>
      </div>
    </>
  )
}


function FragenVorlagenEditPage() {
  return (
    <>
      <AdminSidebar openedTab={"FragenVorlagen"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <FragenVorlagenEdit />
        </Suspense>
      </div>
    </>
  )
}

function EmailVorlagenPage() {
  return (
    <>
      <AdminSidebar openedTab={"EmailVorlagen"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <EmailVorlagen />
        </Suspense>
      </div>
    </>
  )
}

function EmailVorlagenEditPage() {
  return (
    <>
      <AdminSidebar openedTab={"EmailVorlagen"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <EmailVorlagenEdit />
        </Suspense>
      </div>
    </>
  )
}

function MandatierungPage() {
  return (
    <>
      <AdminSidebar openedTab={"mandatierung"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <Mandatierung />
        </Suspense>
      </div>
    </>
  )
}

function FachgesellschaftenGraphPage() {
  return (
    <>
      <AdminSidebar openedTab={"fachgesellschaften"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <FachgesellschaftenGraph />
        </Suspense>
      </div>
    </>
  )
}

function OnkospaceeComPage() {
  return (
    <>
      <AdminSidebar openedTab={"patient_guidelines"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <Onkospace_com />
        </Suspense>
      </div>
    </>
  )
}

function ProfilePage() {
  return (
    <>
      <AdminSidebar variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <Profile />
        </Suspense>
      </div>
    </>
  )
}

function AdminListPage() {
  return (
    <>
      <AdminSidebar openedTab={"AdminList"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <AdminList />
        </Suspense>
      </div>
    </>
  )
}

function AdminCreatePage() {
  return (
    <>
      <AdminSidebar openedTab={"AdminList"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <AdminForm mode="create" />
        </Suspense>
      </div>
    </>
  )
}

function AdminEditPage() {
  return (
    <>
      <AdminSidebar openedTab={"AdminList"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <AdminForm mode="edit" />
        </Suspense>
      </div>
    </>
  )
}

function AuditLogPage() {
  return (
    <>
      <AdminSidebar openedTab={"AuditLog"} variant="v2" />
      <div className="main-panel" style={V2_PANEL_STYLE}>
        <Suspense fallback={<LoadingSpinner />}>
          <AuditLog />
        </Suspense>
      </div>
    </>
  )
}

function App() {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return <LoadingSpinner />
  }

  if (!isAuthenticated) {
    return (
      <RouteErrorBoundary>
        <BrowserRouter>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/ergebnis/abstimmung/v2/:vote_id" element={<VoteResult_PublicPage_V2 />} />
              <Route path="/ergebnis/abstimmung/v2/:vote_id/:tab" element={<VoteResult_PublicPage_V2 />} />
              {/* Coordinator-mode v2 routes , same component, decryption
                  key flips to `dkg_coo` (handled inside PublicResult).
                  Used by the "Koordinaten-Ergebnis" button on each card. */}
              <Route path="/ergebnis/abstimmung/v2/coo/:vote_id" element={<VoteResult_PublicPage_V2 />} />
              <Route path="/ergebnis/abstimmung/v2/coo/:vote_id/:tab" element={<VoteResult_PublicPage_V2 />} />
              <Route path="/ergebnis/abstimmung/v2/coo/:vote_id/*" element={<VoteResult_PublicPage_V2 />} />
              <Route path="/ergebnis/abstimmung/:vote_id" element={<VoteResult_PublicPage />} />
              <Route path="/ergebnis/abstimmung/:vote_id/:tab" element={<VoteResult_PublicPage />} />
              <Route path="/ergebnis/abstimmung/coo/:vote_id" element={<VoteResult_PublicPage />} />
              <Route path="/ergebnis/abstimmung/coo/:vote_id/:tab" element={<VoteResult_PublicPage />} />
              <Route path="/ergebnis/abfrage/:vote_id" element={<VoteResult_PublicPage />} />
              <Route path="/ergebnis/abfrage/:vote_id/:tab" element={<VoteResult_PublicPage />} />
              <Route path="/ergebnis/abfrage/coo/:vote_id" element={<VoteResult_PublicPage />} />
              <Route path="/ergebnis/abfrage/coo/:vote_id/:tab" element={<VoteResult_PublicPage />} />

              <Route path="/brokenurls/:guideline_id" element={<PublicBrokenURLsPage />} />

              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/accept-invite/:token" element={<AcceptInvite />} />
              <Route path="/confirm-email/:token" element={<ConfirmEmail />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />

              <Route path="*" element={<Login />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </RouteErrorBoundary>
    )
  }

  return (
    <RouteErrorBoundary>
      <BrowserRouter>
        <TestBanner />
        <RouteAccessBoundary><Routes>
          <Route path="/" element={<Navigate replace to="/activity" />} />

          <Route exact path="/activity" element={<ActivityPage />} />
          <Route exact path="/authors" element={<AuthorsPage />} />
          <Route exact path="/leitlinien" element={<Custom_live_guidelinePage />} />
          <Route exact path="/leitlinien/create" element={<Create_Custom_live_guidelinePage />} />
          <Route path="/leitlinien/edit/:guidelineId" element={<Edit_Custom_live_guidelinePage />} />

          <Route exact path="/stageleitlinien" element={<StageLeitlinienPage />} />
          <Route exact path="/livearbeitsversionen" element={<LiveLeitlinienPage />} />
          <Route exact path="/qualitaetsindikatoren" element={<QualityIndicatorsPage />} />





          {/*abfragen*/}
          <Route exact path="/abfragen" element={<AbfragenPage />} />
          <Route exact path="/abfrage/bearbeiten/:id" element={<AbfrageEditPage />} />
          <Route exact path="/abfrage/erstellen" element={<AbfrageErstellenPickerV2 />} />
          <Route exact path="/abfrage/erstellen/:guideline_id" element={<AbfrageErstellenPage />} />
          <Route exact path="/abfrage/erstellen/:guideline_id/:template_id" element={<AbfrageErstellenPage />} />
          <Route path="/ergebnis/abfrage/:vote_id" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/abfrage/coo/:vote_id" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/abfrage/coo/:vote_id/*" element={<VoteResult_PublicPage />} />

          <Route exact path="/:type/erstellen/" element={<ErstellenPage />} />

          {/*DEMO*/}
          <Route exact path="/demo" element={<Suspense fallback={<LoadingSpinner />}><DemoInviteVariants /></Suspense>} />

          {/* SIGNUP REDESIGN MOCKUPS — visual mockups only, no persistence */}
          <Route exact path="/signup-redesign" element={
            <Suspense fallback={<LoadingSpinner />}><SignupRedesignHub /></Suspense>
          } />
          <Route exact path="/signup-redesign/wizard" element={
            <Suspense fallback={<LoadingSpinner />}><SignupRedesignWizard /></Suspense>
          } />
          <Route exact path="/signup-redesign/sidebar" element={
            <Suspense fallback={<LoadingSpinner />}><SignupRedesignSidebar /></Suspense>
          } />
          <Route exact path="/signup-redesign/cards" element={
            <Suspense fallback={<LoadingSpinner />}><SignupRedesignCards /></Suspense>
          } />
          <Route exact path="/signup-redesign/sidebar-lab" element={
            <Suspense fallback={<LoadingSpinner />}><SidebarLab /></Suspense>
          } />
          <Route exact path="/signup-redesign/fragen-lab" element={
            <Suspense fallback={<LoadingSpinner />}><FragenLab /></Suspense>
          } />
          <Route exact path="/signup-redesign/vote-tw" element={
            <Suspense fallback={<LoadingSpinner />}><VoteTailwind /></Suspense>
          } />
          <Route exact path="/design-system" element={
            <Suspense fallback={<LoadingSpinner />}><DesignSystem /></Suspense>
          } />
          <Route exact path="/signup-redesign/vote-card-concepts" element={
            <Suspense fallback={<LoadingSpinner />}><VoteCardConcepts /></Suspense>
          } />

          {/* SIGNUP V2 — production rebuild using Claude-Design handoff. Same API as v1.
              /<type>/erstellen (no params)   → v2 picker (single-page guideline selection)
              /<type>/erstellen/:guideline_id → v2 form (SignupV2 in create mode)
              The legacy /erstellen_v2/* paths still work via redirect. */}
          <Route exact path="/abstimmung/bearbeiten_v2/:id"                              element={<AbstimmungEditPageV2 />} />
          <Route exact path="/abfrage/bearbeiten_v2/:id"                                 element={<AbfrageEditPageV2 />} />
          <Route exact path="/konsultation/bearbeiten_v2/:id"                            element={<KonsultationEditPageV2 />} />

          {/* Aliases: old _v2 URLs redirect to the canonical /erstellen path */}
          <Route exact path="/abstimmung/erstellen_v2"                                   element={<AbstimmungErstellenV2Redirect />} />
          <Route exact path="/abstimmung/erstellen_v2/:guideline_id"                     element={<AbstimmungErstellenV2Redirect />} />
          <Route exact path="/abstimmung/erstellen_v2/:guideline_id/:template_id"        element={<AbstimmungErstellenV2Redirect />} />
          <Route exact path="/abfrage/erstellen_v2"                                      element={<AbfrageErstellenV2Redirect />} />
          <Route exact path="/abfrage/erstellen_v2/:guideline_id"                        element={<AbfrageErstellenV2Redirect />} />
          <Route exact path="/abfrage/erstellen_v2/:guideline_id/:template_id"           element={<AbfrageErstellenV2Redirect />} />
          <Route exact path="/konsultation/erstellen_v2"                                 element={<KonsultationErstellenV2Redirect />} />
          <Route exact path="/konsultation/erstellen_v2/:guideline_id"                   element={<KonsultationErstellenV2Redirect />} />
          <Route exact path="/konsultation/erstellen_v2/:guideline_id/:template_id"      element={<KonsultationErstellenV2Redirect />} />

          {/*VOTE*/}
          <Route exact path="/abstimmungen" element={<AbstimmungenPage />} />
          <Route exact path="/abstimmungen-alt" element={<AbstimmungenLegacyPage />} />
          <Route exact path="/abstimmung/bearbeiten/:id" element={<AbstimmungEditPage />} />
          <Route exact path="/abstimmung/erstellen" element={<AbstimmungErstellenPickerV2 />} />
          <Route exact path="/abstimmung/erstellen/:guideline_id" element={<AbstimmungErstellenPage />} />
          <Route exact path="/abstimmung/erstellen/:guideline_id/:template_id" element={<AbstimmungErstellenPage />} />
          
          <Route path="/ergebnis/abstimmung/v2/:vote_id" element={<VoteResult_PublicPage_V2 />} />
          <Route path="/ergebnis/abstimmung/v2/:vote_id/:tab" element={<VoteResult_PublicPage_V2 />} />
          <Route path="/ergebnis/abstimmung/v2/coo/:vote_id" element={<VoteResult_PublicPage_V2 />} />
          <Route path="/ergebnis/abstimmung/v2/coo/:vote_id/:tab" element={<VoteResult_PublicPage_V2 />} />
          <Route path="/ergebnis/abstimmung/v2/coo/:vote_id/*" element={<VoteResult_PublicPage_V2 />} />
          <Route path="/ergebnis/abstimmung/:vote_id" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/abstimmung/:vote_id/:tab" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/abstimmung/coo/:vote_id" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/abstimmung/coo/:vote_id/:tab" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/abstimmung/coo/:vote_id/:tab/:element_uid" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/abstimmung/coo/:vote_id/*" element={<VoteResult_PublicPage />} />

          <Route exact path="/konsultation" element={<ConsultationPage />} />
          <Route exact path="/konsultation/erstellen" element={<KonsultationErstellenPickerV2 />} />
          <Route exact path="/konsultation/erstellen/:guideline_id" element={<KonsultationErstellenPage />} />
          <Route exact path="/konsultation/erstellen/:guideline_id/:tab" element={<KonsultationErstellenPage />} />
          <Route exact path="/konsultation/bearbeiten/:id" element={<KonsultationEditPage />} />
          <Route path="/ergebnis/konsultation/:vote_id" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/konsultation/:vote_id/:tab" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/konsultation/:vote_id/:tab/:element_uid" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/konsultation/coo/:vote_id/" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/konsultation/coo/:vote_id/:tab" element={<VoteResult_PublicPage />} />
          <Route path="/ergebnis/konsultation/coo/:vote_id/:tab/:element_uid" element={<VoteResult_PublicPage />} />
          


          <Route path="/deepsearch" element={<DeepSearchPage />} />
          <Route exact path="/leitlinien-umfragen" element={<GuidelineSurveysPage />} />
          <Route exact path="/leitlinien-umfragen/editor/:id" element={<SurveyEditorPage />} />
          <Route exact path="/leitlinien-umfragen/editor/:id/settings" element={<SurveyEditorSettingsRedirect />} />
          <Route exact path="/leitlinien-umfragen/results/:id" element={<SurveyResultsPage />} />
          <Route exact path="/leitlinien-kommentare" element={<GuidelineCommentsPage />} />
          <Route exact path="/app-benachrichtigungen" element={<AppPushPage />} />
          <Route exact path="/app-abonnenten" element={<Navigate replace to="/app-analytics" />} />
          <Route exact path="/app-analytics" element={<AppAnalyticsPage />} />
          <Route path="/newsletter" element={<NewsletterPage />} />
          <Route path="/newsletter/compose/:campaignId" element={<NewsletterComposePage />} />
          <Route path="/newsletter/compose" element={<NewsletterComposePage />} />
          <Route path="/brokenurls/:guideline_id" element={<PrivateBrokenURLsGuidelinePage />} />
          <Route path="/brokenurls-progress/:guideline_id/:pid" element={<BrokenUrlsProgressPage />} />
          <Route path="/brokenurls-ignored" element={<BrokenUrlsIgnoredPage />} />
          <Route exact path="/brokenurls" element={<BrokenURLsPage />} />
          <Route path="/newsletter/history" element={<NewsletterHistoryPage />} />
          <Route path="/newsletter/transactional" element={<NewsletterTransactionalPage />} />

          

          <Route path="/servermetrics" element={<ServerMetricsPage />} />
          <Route path="/hub-analytics" element={<HubAnalyticsPage />} />
          <Route path="/abstimmungen-analytics" element={<AbstimmungsAnalyticsPage />} />
          <Route path="/feedback" element={<VoteFeedbackPage />} />
          <Route path="/email-zustellung" element={<EmailDeliverabilityPage />} />
          


          {/*<Route exact path="/konsultationen" element={<KonsultationenPage />} />*/}
          {/* Legacy alias — the unified /vorlagen page was split into
              /emailvorlagen (Tools) and /fragenvorlagen (Umfragen). */}
          <Route exact path="/vorlagen" element={<Navigate to="/emailvorlagen" replace />} />
          <Route exact path="/fragenvorlagen" element={<FragenVorlagenPage />} />
          <Route path="/fragenvorlagen/new" element={<FragenVorlagenEditPage />} />
          <Route path="/fragenvorlagen/:id/edit" element={<FragenVorlagenEditPage />} />

          <Route exact path="/emailvorlagen" element={<EmailVorlagenPage />} />
          <Route path="/emailvorlagen/new" element={<EmailVorlagenEditPage />} />
          <Route path="/emailvorlagen/:id/edit" element={<EmailVorlagenEditPage />} />
          <Route exact path="/mandatierung" element={<MandatierungPage />} />
          <Route exact path="/fachgesellschaftengraph" element={<FachgesellschaftenGraphPage />} />
          <Route exact path="/hub-leitlinien" element={<OnkospaceeComPage />} />


          <Route exact path="/errorsdisplay" element={<ErrorsDisplayPage />} />
          <Route exact path="/backups" element={<BackupsPage />} />


          <Route exact path="/leitlinie/" element={<UserVote_TestPage />} />
          <Route path="/leitlinie/:url/*" element={<UserVote_TestPage />} />

          {/*<Route path="/ergebnis/consultation/coo/:consultation_id" element={<ConsultationResult_internaalPage />} />*/}
          {/*<Route path="/ergebnis/consultation/:consultation_id" element={<ConsultationResult_internaalPage />} />*/}

          <Route exact path="/Fachgesellschaften" element={<FachgesellschaftenRedirect />} />
          <Route exact path="/fachgesellschaften/leitlinie/:guidline_id" element={<FachgesellschaftenLeitlinieRedirect />} />


          <Route exact path="/impressum" element={<ImpressumPage />} />
          <Route exact path="/datenschutz" element={<DatenschutzPage />} />
          <Route exact path="/unsubscribe" element={<UnsubscribePage />} />

          <Route exact path="/test" element={<Test />} />

          <Route exact path="/profile" element={<ProfilePage />} />
          {/* Email-change confirmation is initiated from /profile while logged in, so the
              admin clicking the link is still authenticated — the route must exist here too,
              not only in the unauthenticated branch above. (ConfirmEmail is self-contained.) */}
          <Route path="/confirm-email/:token" element={<Suspense fallback={<LoadingSpinner />}><ConfirmEmail /></Suspense>} />
          <Route exact path="/admin-verwaltung" element={<AdminListPage />} />
          <Route exact path="/admin-verwaltung/neu" element={<AdminCreatePage />} />
          <Route exact path="/admin-verwaltung/:id" element={<AdminEditPage />} />
          <Route exact path="/audit-log" element={<AuditLogPage />} />

          <Route exact path="*" element={<NotFound />} />
        </Routes></RouteAccessBoundary>
      </BrowserRouter>
    </RouteErrorBoundary>
  )
}

export default App
